import { useEffect } from 'react';

interface ArticleSchemaProps {
  type: 'article';
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  url: string;
  image?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  type: 'faq';
  items: FAQItem[];
}

interface SoftwareSchemaProps {
  type: 'software';
  name: string;
  description: string;
  url: string;
  category?: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  type: 'breadcrumb';
  items: BreadcrumbItem[];
}

type SchemaProps = ArticleSchemaProps | FAQSchemaProps | SoftwareSchemaProps | BreadcrumbSchemaProps;

function buildSchema(props: SchemaProps) {
  if (props.type === 'article') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: props.title,
      description: props.description,
      datePublished: props.datePublished,
      dateModified: props.dateModified || props.datePublished,
      url: props.url,
      image: props.image || 'https://sorvaine.com/og-image.png',
      author: { '@type': 'Organization', name: 'Sorvaine' },
      publisher: {
        '@type': 'Organization',
        name: 'Sorvaine',
        url: 'https://sorvaine.com',
      },
    };
  }

  if (props.type === 'faq') {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: props.items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    };
  }

  if (props.type === 'software') {
    return {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: props.name,
      description: props.description,
      url: props.url,
      applicationCategory: props.category || 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'GBP',
        description: 'Book a demo for pricing',
      },
    };
  }

  if (props.type === 'breadcrumb') {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: props.items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        item: item.url,
      })),
    };
  }
}

export function SchemaMarkup(props: SchemaProps) {
  const schema = buildSchema(props);

  useEffect(() => {
    const id = `schema-${props.type}-${Date.now()}`;
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [schema, props.type]);

  return null;
}

export type { FAQItem, BreadcrumbItem };
